/**
 * Firebase Web SDK singletons.
 *
 * The SPA reads Firestore directly via the modular Web SDK (real-time listeners)
 * and authenticates operators with Google SSO. The admin read rule
 * (`isPlatformAdmin()`) is enforced server-side by Firestore security rules, so
 * the browser only ever sees collections the operator's `roles:['admin']` token
 * is allowed to read.
 */
import { initializeApp } from "firebase/app";
import { GoogleAuthProvider, getAuth, setPersistence, browserLocalPersistence } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { env } from "./env";

export const firebaseApp = initializeApp(env.firebase);

export const auth = getAuth(firebaseApp);
// Operators stay signed in across reloads; tokens are short-lived and refreshed
// by the SDK, and the admin claim is re-checked on every gated action.
void setPersistence(auth, browserLocalPersistence);

export const db = getFirestore(firebaseApp);

/**
 * Google provider configured for Workspace SSO. When a hosted-domain is set we
 * pin it via the `hd` parameter so the account chooser only offers Workspace
 * accounts (UX only — the backend + rules are the real boundary).
 */
export function googleProvider(): GoogleAuthProvider {
  const provider = new GoogleAuthProvider();
  if (env.allowedHostedDomain) {
    provider.setCustomParameters({ hd: env.allowedHostedDomain, prompt: "select_account" });
  } else {
    provider.setCustomParameters({ prompt: "select_account" });
  }
  return provider;
}
